'use client'

import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useIsHydrated } from '@maestro/commerce-blocks/hooks/use-is-hydrated'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType, type NavigationItem } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'
import { useBreakpoint } from '@pearlconvert/ui/providers/breakpoint/breakpoint.context'

const MEGA_MENU_MIN_DEPTH = 2

export const useNavigationDesktopItem = (node: NavigationItem) => {
  const [isActive, setIsActive] = useState(false)
  const [isFirstMouseClick, setIsFirstMouseClick] = useState(true)
  const itemRef = useRef<HTMLLIElement>(null)
  const pathname = usePathname()
  const { publish } = useEventBusContext()
  const { isDesktopView } = useBreakpoint()
  const { openOnClick: isOpenOnClick } = theme().navigation
  const isHydrated = useIsHydrated()

  const publishMenuClick = useCallback(
    (clickedNode: NavigationItem, parentPath: string[] = []) => {
      const level1 = parentPath[0] || clickedNode.title || undefined
      const level2 =
        parentPath[1] || (parentPath.length > 0 ? clickedNode.title : undefined)
      const level3 = parentPath.length > 1 ? clickedNode.title : undefined

      publish(EventType.MENU_CLICKED, {
        level1,
        level2,
        level3,
      })
    },
    [publish]
  )

  const isMegaDropdown = useCallback((depth: number) => {
    return depth >= MEGA_MENU_MIN_DEPTH
  }, [])

  const toggleDropdown = useCallback(() => {
    setIsActive((prev) => !prev)
  }, [])

  const handleControlKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        e.preventDefault()
        setIsActive(false)
        // Focus stays on the link
      }
    },
    [isActive]
  )

  const openDropdown = useCallback(() => {
    setIsActive(true)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsActive(false)
    setIsFirstMouseClick(true)
  }, [])

  const handleClickLink = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // If item has children, toggle dropdown on first click, navigate on second
      if (isOpenOnClick && node.children?.length) {
        e.preventDefault()
        if (isFirstMouseClick) {
          setIsFirstMouseClick(false)
          toggleDropdown()
        } else {
          publishMenuClick(node)
          setIsFirstMouseClick(true)
          // Blur the link to remove focus after navigation
          ;(e.currentTarget as HTMLElement).blur()
        }
      } else {
        publishMenuClick(node)
        // Blur the link to remove focus after navigation
        ;(e.currentTarget as HTMLElement).blur()
      }
    },
    [isFirstMouseClick, isOpenOnClick, node, publishMenuClick, toggleDropdown]
  )

  const handleMouseEnter = useCallback(() => {
    if (!isOpenOnClick) {
      openDropdown()
    }
  }, [isOpenOnClick, openDropdown])

  const handleMouseLeave = useCallback(() => {
    if (!isOpenOnClick) {
      closeDropdown()
    }
  }, [isOpenOnClick, closeDropdown])

  const handleBlur = useCallback(
    (target: (EventTarget & Element) | null) => {
      // Close dropdown when focus leaves the entire menu item (including submenu)
      setTimeout(() => {
        if (!target || !itemRef.current?.contains(target as Node)) {
          const activeElement = document.activeElement
          if (!itemRef.current?.contains(activeElement)) {
            closeDropdown()
          }
        }
      }, 0)
    },
    [closeDropdown]
  )

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        itemRef.current &&
        !itemRef.current.contains(event.target as HTMLElement)
      ) {
        closeDropdown()
      }
    },
    [closeDropdown]
  )

  useEffect(() => {
    if (isActive) {
      document.addEventListener('click', handleClickOutside)
    } else {
      document.removeEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isActive, handleClickOutside])

  useEffect(() => {
    setIsActive(false)
  }, [pathname])

  return {
    isActive,
    itemRef,
    closeDropdown,
    handleBlur,
    handleClickLink,
    handleControlKeyDown,
    handleMouseEnter,
    handleMouseLeave,
    isDesktopView: isDesktopView() && isHydrated,
    isMegaDropdown,
  }
}
